/**
 * Whether an element the kit is about to move the focus to is shown at all
 * (review BLP/nx#3198 171906, finding 5): an attribute (`hidden`, `inert`,
 * `aria-hidden`) is not the only way to hide a field — `display: none` on it
 * or on an ancestor (`v-show`) or `visibility: hidden` hide it too, and
 * `.focus()` on such an element does nothing. A browser answers with
 * `checkVisibility`; where it has none (jsdom), the computed styles of the
 * element and its ancestors are read.
 */
export function isNxShown(el: Element): boolean {
  if (el.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
  const check = (el as Element & { checkVisibility?: (o?: object) => boolean }).checkVisibility;
  if (typeof check === 'function') {
    return check.call(el, { visibilityProperty: true });
  }
  const view = el.ownerDocument?.defaultView;
  if (!view) return true;
  if (view.getComputedStyle(el).visibility === 'hidden') return false;
  for (let node: Element | null = el; node; node = node.parentElement) {
    if (view.getComputedStyle(node).display === 'none') return false;
  }
  return true;
}

/**
 * Focuses the first of `candidates` that really takes the focus — a candidate
 * whose `.focus()` did nothing (hidden in a way no filter saw) is passed over
 * — and returns it, or `null` when none did.
 */
export function focusFirstOf(candidates: readonly HTMLElement[]): HTMLElement | null {
  for (const candidate of candidates) {
    candidate.focus();
    if (candidate.ownerDocument.activeElement === candidate) return candidate;
  }
  return null;
}
